"use client"
import styled from "@emotion/styled"
import { useId } from "react"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import "swiper/css/pagination"
import { Swiper, SwiperSlide } from "swiper/react"
import { NewText, transitionDelayTime, TransitionUp, useInViewConfig } from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { sharedColors } from "ui/theme/colors"
import { RentProjectCustomerReviewItem, RentProjectCustomerReviews } from "utils/projects"
import WhiteBgHeading from "./WhiteBgHeading"

const StyledCustomerReviews = styled.section`
  width: 100%;
  padding-block-start: 60px;
  display: flex;
  flex-direction: column;
  padding-inline: 20px;
`

const SliderContainer = styled.div<{ locale: string }>`
  width: 100%;
  display: flex;
  overflow: hidden;
  position: relative;
  align-self: stretch;

  &::after,
  &::before {
    display: flex;
    position: absolute;
    z-index: 2;
    bottom: -26px;
    width: 155px;
    height: 725px;
    ${(props) => props.theme.breakpoints.up("md")} {
      content: "";
    }
  }

  &::after {
    inset-inline-start: 0;
    background: ${(props) =>
    props.locale === "he" ? "var(--after-gradient)" : "var(--before-gradient)"};
  }

  &::before {
    inset-inline-end: 0;
    background: ${(props) =>
    props.locale === "he" ? "var(--before-gradient)" : "var(--after-gradient)"};
  }
`

const StyledCustomerReviewItem = styled.div`
  --text-black: #011727;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px;
  color: var(--text-black);
  border-radius: 8px;
  border: 1px solid #b5bfc9;
  background: white;
  height: 356px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 364px;
  }
`
const ProfileImageWrapper = styled.div`
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  aspect-ratio: 1/1;
  width: 84px;
  height: 84px;
`

const StarsImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 28px;
  width: 100%;
  margin-bottom: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 0;
  }
`

const ReviewItemHeadings = styled.div`
  text-align: center;
`
const StyledReviewContent = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: var(--text-black) inherit;
  text-align: center;
  margin: 0;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 4;
  white-space: normal;

  p {
    margin: 0;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
  }
`

const StyledSwiper = styled(Swiper)`
  padding-block: 60px;
`

type CustomerReviewItemProps = {
  item: RentProjectCustomerReviewItem
}

const CustomerReviewItem = ({ item }: CustomerReviewItemProps) => {
  const title = item?.title
  const subTitle = item?.subTitle
  const starsImage = item?.stars

  const content = item?.content
  const authorImage = item?.image
  return (
    <StyledCustomerReviewItem>
      {starsImage && (
        <StarsImageContainer>
          <ImageWithAlt
            image={{
              url: starsImage,
              altText: "Rating stars",
            }}
            width={158}
            height={28}
          />
        </StarsImageContainer>
      )}
      {authorImage && (
        <ProfileImageWrapper>
          <ImageWithAlt
            image={{
              url: authorImage,
              altText: "Author",
            }}
            width={84}
            height={84}
          />
        </ProfileImageWrapper>
      )}
      <ReviewItemHeadings>
        {title && (
          <NewText
            component="h5"
            desktopFontSize={24}
            mobileFontWeight={300}
            desktopFontWeight={400}
            desktopLineHeight={"150%"}
            letterSpacing={"-0.48px"}
            mobileFontSize={20}
            mobileLineHeight={"130%"}
            color={sharedColors.darkBlue4rentMarketing}
            sx={{
              margin: 0,
              marginBottom: 1,
            }}
          >
            {title}
          </NewText>
        )}
        {subTitle && (
          <NewText
            component="p"
            desktopFontSize={18}
            mobileFontWeight={300}
            desktopFontWeight={300}
            desktopLineHeight={"150%"}
            letterSpacing={"-0.36px"}
            mobileFontSize={16}
            mobileLineHeight={"145%"}
            color="inherit"
            sx={{
              margin: 0,
            }}
          >
            {subTitle}
          </NewText>
        )}
      </ReviewItemHeadings>
      {content && (
        <StyledReviewContent
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
      )}
    </StyledCustomerReviewItem>
  )
}

type Props = {
  data: RentProjectCustomerReviews
  locale: string
}

const CustomerReviews = ({ data, locale }: Props) => {
  const uniqueId = useId()
  const { ref, inView } = useInView(useInViewConfig)

  const title = data?.title
  const subTitle = data?.subTitle
  const items = data?.reviews

  return (
    <TransitionUp ref={ref} $inView={inView} $delay={transitionDelayTime}>
      <StyledCustomerReviews>
        <WhiteBgHeading title={title} subTitle={subTitle} />
        <SliderContainer
          locale={locale}
          style={
            {
              "--after-gradient": "linear-gradient(to left,#ffffff 0%,rgba(255,255,255,0.26) 100%)",
              "--before-gradient":
                "linear-gradient(to right,#ffffff 0%,rgba(255,255,255,0.26) 100%)",
            } as React.CSSProperties
          }
        >
          <StyledSwiper
            dir={locale === "he" ? "rtl" : "ltr"}
            slidesPerView={1.1}
            spaceBetween={24}
            breakpoints={{
              440: {
                slidesPerView: 2,
              },
              768: {
                slidesPerView: 3,
              },
              1366: {
                slidesPerView: 4,
              },
            }}
          >
            {items?.map((item, index) => (
              <SwiperSlide key={`${uniqueId}-${item.title}-${index}`}>
                <CustomerReviewItem item={item} />
              </SwiperSlide>
            ))}
          </StyledSwiper>
        </SliderContainer>
      </StyledCustomerReviews>
    </TransitionUp>
  )
}
export default CustomerReviews
